/**
 * Voice / calling settings — React Query hooks. Low-churn config, so no polling;
 * lists refetch on mutation. Automation/receptionist/IVR are CS-backed (proxied
 * through operations); agent flags are operations-native.
 */

import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useApiMutation } from '@/hooks/useApiMutation';
import { voiceKeys } from '@/lib/queryKeys';
import { voiceApi, type CallSoundSlot } from './api';
import type { UpsertCallFlowPayload } from './call-flow-types';
import type {
  AccountCallSchedule,
  AssignableNumber,
  AgentVoiceFlags,
  Automation,
  CreateLandlinePayload,
  ReceptionistConfig,
  UpdateAccountSchedulePayload,
  UpdateAutomationPayload,
  UpdateNumberRoutingPayload,
  UpdateReceptionistPayload,
  UpsertDirectoryPayload,
  UpsertIvrPayload,
} from './types';

/**
 * Calling config barely changes within a session, and every tab plus the shared
 * overview reads the same queries. A stale window keeps a tab switch (or the
 * overview + a section sharing a key) from refetching — one load per key until
 * a mutation invalidates it. (In dev, React StrictMode still double-mounts, so
 * you'll see two initial requests there; production makes one.)
 */
const VOICE_STALE_MS = 60_000;

// ─── Agent flags ─────────────────────────────────────────────────────────────

export function useVoiceAgents(enabled = true) {
  return useApiQuery({
    queryKey: voiceKeys.agents(),
    queryFn: () => voiceApi.agentSettings.list(),
    showErrorToast: false,
    staleTime: VOICE_STALE_MS,
    // Skip the fetch when the caller lacks `users:view` (agent roster is
    // user-scoped) so the page never fires a request that would 403.
    enabled,
  });
}

export function useUpdateAgentVoiceSettings() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: ({ userId, flags }: { userId: string; flags: AgentVoiceFlags }) =>
      voiceApi.agentSettings.update(userId, flags),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: voiceKeys.agents() });
    },
  });
}

// ─── Automation ──────────────────────────────────────────────────────────────

export function useAutomation() {
  return useApiQuery({
    queryKey: voiceKeys.automation(),
    queryFn: () => voiceApi.automation.get(),
    showErrorToast: false,
    staleTime: VOICE_STALE_MS,
  });
}

export function useUpdateAutomation() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: (payload: UpdateAutomationPayload) => voiceApi.automation.update(payload),
    onSuccess: (data: Automation) => {
      qc.setQueryData(voiceKeys.automation(), data);
    },
  });
}

// ─── AI receptionist ─────────────────────────────────────────────────────────

export function useReceptionist(enabled = true) {
  return useApiQuery({
    queryKey: voiceKeys.receptionist(),
    queryFn: () => voiceApi.receptionist.get(),
    showErrorToast: false,
    staleTime: VOICE_STALE_MS,
    enabled,
  });
}

export function useUpdateReceptionist() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: (payload: UpdateReceptionistPayload) => voiceApi.receptionist.update(payload),
    onSuccess: (data: ReceptionistConfig) => {
      qc.setQueryData(voiceKeys.receptionist(), data);
    },
  });
}

// ─── IVR / call tree ─────────────────────────────────────────────────────────

export function useIvrTrees(all = false) {
  return useApiQuery({
    queryKey: [...voiceKeys.ivr(), { all }],
    queryFn: () => voiceApi.ivr.list(all),
    showErrorToast: false,
    staleTime: VOICE_STALE_MS,
  });
}

export function useCreateIvr() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: (payload: UpsertIvrPayload) => voiceApi.ivr.create(payload),
    onSuccess: () => void qc.invalidateQueries({ queryKey: voiceKeys.ivr() }),
  });
}

export function useUpdateIvr() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpsertIvrPayload }) =>
      voiceApi.ivr.update(id, payload),
    onSuccess: () => void qc.invalidateQueries({ queryKey: voiceKeys.ivr() }),
  });
}

export function useDeleteIvr() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: (id: string) => voiceApi.ivr.remove(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: voiceKeys.ivr() }),
  });
}

export function useActivateIvr() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      active ? voiceApi.ivr.activate(id) : voiceApi.ivr.deactivate(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: voiceKeys.ivr() }),
  });
}

/** Upload a greeting clip (.mp3/.wav) → returns { url } to store as the greeting. */
export function useUploadIvrAudio() {
  return useApiMutation({
    mutationFn: (file: File) => voiceApi.ivr.uploadAudio(file),
  });
}

/**
 * Something an `<audio>` element can play for one of the account's sounds,
 * without the page ever holding the address it is stored at: the bytes come
 * through our API and are handed over as a local `blob:` URL.
 *
 * `src` is null while loading and whenever `enabled` is false; `failed` means
 * there was nothing this route could play, which for a link the account pasted
 * in itself is expected (operations only streams audio that lives with us).
 */
export function useCallSoundSrc(slot: CallSoundSlot, enabled = true) {
  const [state, setState] = useState<{ src: string | null; failed: boolean }>({
    src: null,
    failed: false,
  });

  useEffect(() => {
    setState({ src: null, failed: false });
    if (!enabled) return;
    let live = true;
    let objectUrl: string | null = null;
    voiceApi.callSounds
      .fetch(slot)
      .then((blob) => {
        if (!live) return;
        objectUrl = URL.createObjectURL(blob);
        setState({ src: objectUrl, failed: false });
      })
      .catch(() => {
        if (live) setState({ src: null, failed: true });
      });
    return () => {
      live = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [slot, enabled]);

  return state;
}

// ─── Directory: dialable extensions ──────────────────────────────────────────

export function useDirectory(enabled = true) {
  return useApiQuery({
    queryKey: voiceKeys.directory(),
    queryFn: () => voiceApi.directory.list(),
    staleTime: VOICE_STALE_MS,
    enabled,
  });
}

export function useCreateDirectoryEntry() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: (payload: UpsertDirectoryPayload) => voiceApi.directory.create(payload),
    onSuccess: () => void qc.invalidateQueries({ queryKey: voiceKeys.directory() }),
  });
}

export function useUpdateDirectoryEntry() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpsertDirectoryPayload }) =>
      voiceApi.directory.update(id, payload),
    onSuccess: () => void qc.invalidateQueries({ queryKey: voiceKeys.directory() }),
  });
}

export function useDeleteDirectoryEntry() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: (id: string) => voiceApi.directory.remove(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: voiceKeys.directory() }),
  });
}

// ─── Number assignment ───────────────────────────────────────────────────────

export function useAssignableNumbers(enabled = true) {
  return useApiQuery({
    queryKey: voiceKeys.numbers(),
    queryFn: () => voiceApi.numbers.list(),
    showErrorToast: false,
    staleTime: VOICE_STALE_MS,
    enabled,
  });
}

/**
 * Optimistic, because the row toggle is a switch and a switch that lags a
 * round-trip behind the click reads as broken. The patch is merged the same way
 * the CS merges it (partial), and the previous list goes back on failure — a
 * screen that keeps claiming a routing the server refused is worse than a
 * flicker.
 */
export function useUpdateNumberRouting() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateNumberRoutingPayload }) =>
      voiceApi.numbers.updateRouting(id, patch),
    // The section names rejected team members itself from `data.unknown`.
    showErrorToast: false,
    onMutate: async ({ id, patch }) => {
      await qc.cancelQueries({ queryKey: voiceKeys.numbers() });
      const previous = qc.getQueryData<AssignableNumber[]>(voiceKeys.numbers());
      qc.setQueryData<AssignableNumber[]>(voiceKeys.numbers(), (list) =>
        list?.map((n) =>
          n._id === id ? { ...n, numberRouting: { ...n.numberRouting, ...patch } } : n,
        ),
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) qc.setQueryData(voiceKeys.numbers(), context.previous);
    },
    // Refetched either way: the list also carries assignee names and devices,
    // which only the server can fill in for someone newly picked.
    onSettled: () => void qc.invalidateQueries({ queryKey: voiceKeys.numbers() }),
  });
}

// ─── Landlines: desk phones / ATAs ───────────────────────────────────────────

export function useLandlines(enabled = true) {
  return useApiQuery({
    queryKey: voiceKeys.landlines(),
    queryFn: () => voiceApi.landlines.list(),
    showErrorToast: false,
    staleTime: VOICE_STALE_MS,
    enabled,
  });
}

/** How often an open Desk phones tab asks again. The calling service caches for as long. */
const LANDLINE_REGISTRATION_POLL_MS = 30_000;

/**
 * Whether each desk phone is signed in to Telnyx. Polled while the screen is
 * open, because plugging a handset in is exactly when someone is watching this.
 * A failure is silent: the chips fall back to "Checking", never an error toast.
 */
export function useLandlineRegistration(enabled = true) {
  return useApiQuery({
    queryKey: voiceKeys.landlineRegistration(),
    queryFn: () => voiceApi.landlines.registration(),
    showErrorToast: false,
    staleTime: LANDLINE_REGISTRATION_POLL_MS,
    refetchInterval: LANDLINE_REGISTRATION_POLL_MS,
    enabled,
  });
}

export function useCreateLandline() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: (payload: CreateLandlinePayload) => voiceApi.landlines.create(payload),
    // The section names the offending numbers itself from `data.notOwned` /
    // `data.unparseable`, so the generic toast would only duplicate it worse.
    showErrorToast: false,
    onSuccess: () => void qc.invalidateQueries({ queryKey: voiceKeys.landlines() }),
  });
}

export function useUpdateLandlineNumbers() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: ({ id, ringsForNumbers }: { id: string; ringsForNumbers: string[] }) =>
      voiceApi.landlines.setNumbers(id, ringsForNumbers),
    showErrorToast: false,
    onSuccess: () => void qc.invalidateQueries({ queryKey: voiceKeys.landlines() }),
  });
}

/**
 * Which of the account's numbers a desk phone DIALS OUT AS.
 *
 * Separate from `useUpdateLandlineNumbers`, which is what it answers for. Both
 * refresh the list, because the card shows the outbound number on its face.
 */
export function useSetLandlineCallerId() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: ({ id, phoneNumber }: { id: string; phoneNumber: string }) =>
      voiceApi.landlines.setCallerId(id, phoneNumber),
    // Same reason as the others here: the dialog names the refusal in place, next
    // to the picker that caused it, which a floating toast cannot do.
    showErrorToast: false,
    onSuccess: () => void qc.invalidateQueries({ queryKey: voiceKeys.landlines() }),
  });
}

export function useDeleteLandline() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: (id: string) => voiceApi.landlines.remove(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: voiceKeys.landlines() }),
  });
}

// ─── Account-wide phone hours ────────────────────────────────────────────────

export function useAccountSchedule(enabled = true) {
  return useApiQuery({
    queryKey: voiceKeys.accountSchedule(),
    queryFn: () => voiceApi.accountSchedule.get(),
    showErrorToast: false,
    staleTime: VOICE_STALE_MS,
    enabled,
  });
}

export function useUpdateAccountSchedule() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: (payload: UpdateAccountSchedulePayload) => voiceApi.accountSchedule.update(payload),
    // The section renders the CS's own field-specific message inline; a toast
    // saying the same thing twice is worse than saying it once, in place.
    showErrorToast: false,
    onSuccess: (data: AccountCallSchedule) => {
      qc.setQueryData(voiceKeys.accountSchedule(), data);
    },
  });
}

// ─── IVR analytics ───────────────────────────────────────────────────────────

/** Per-menu funnel for a date range. Defaults to the last 30 days server-side. */
export function useIvrAnalytics(range: { from?: string; to?: string } = {}, enabled = true) {
  return useApiQuery({
    queryKey: voiceKeys.ivrAnalytics(range),
    queryFn: () => voiceApi.ivrAnalytics.get(range),
    staleTime: VOICE_STALE_MS,
    enabled,
  });
}

// ─── Call flows ──────────────────────────────────────────────────────────────

export function useCallFlows(enabled = true) {
  return useApiQuery({
    queryKey: voiceKeys.callFlows(),
    queryFn: () => voiceApi.callFlows.list(),
    staleTime: VOICE_STALE_MS,
    enabled,
  });
}

export function useCallFlow(id: string | undefined) {
  return useApiQuery({
    queryKey: voiceKeys.callFlow(id),
    queryFn: () => voiceApi.callFlows.get(id as string),
    staleTime: VOICE_STALE_MS,
    enabled: Boolean(id),
  });
}

/**
 * Every mutation invalidates the LIST as well as the one flow.
 *
 * Publishing takes its numbers off whatever flow held them, so a sibling that
 * was live a moment ago may now be a draft holding fewer numbers. Refreshing
 * only the edited flow would leave the list showing a "Live" badge on a flow
 * that no longer answers anything.
 */
function useCallFlowMutation<TArgs, TResult>(
  fn: (args: TArgs) => Promise<TResult>,
  /** Where the flow's id lives in this mutation's arguments, when it has one. */
  idOf?: (args: TArgs) => string | undefined,
) {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: fn,
    onSuccess: (_data: TResult, args: TArgs) => {
      void qc.invalidateQueries({ queryKey: voiceKeys.callFlows() });

      // The DETAIL query, named explicitly rather than relied upon.
      //
      // `callFlow(id)` is currently `[...callFlows(), id]`, and invalidateQueries
      // matches by prefix, so the line above already reaches it — which is why
      // the builder is not in fact showing stale state today. That is a fact
      // about the shape of two key builders, though, not a decision anyone
      // wrote down: reorder `callFlow` so the id no longer sits under
      // `callFlows`, and every flow detail silently stops refreshing after a
      // publish, with nothing failing to say so.
      //
      // Naming it costs one call and removes that coupling.
      const id = idOf?.(args);
      if (id) void qc.invalidateQueries({ queryKey: voiceKeys.callFlow(id) });
    },
  });
}

export function useCreateCallFlow() {
  return useCallFlowMutation((payload: UpsertCallFlowPayload) =>
    voiceApi.callFlows.create(payload),
  );
}

export function useUpdateCallFlow() {
  return useCallFlowMutation(
    ({ id, payload }: { id: string; payload: UpsertCallFlowPayload }) =>
      voiceApi.callFlows.update(id, payload),
    ({ id }) => id,
  );
}

export function useDeleteCallFlow() {
  // No detail key: the flow is gone, so there is nothing to refresh.
  return useCallFlowMutation((id: string) => voiceApi.callFlows.remove(id));
}

export function useDeleteCallFlowStep() {
  return useCallFlowMutation(
    ({ id, stepId, replaceWith }: { id: string; stepId: string; replaceWith?: string }) =>
      voiceApi.callFlows.removeStep(id, stepId, replaceWith),
    ({ id }) => id,
  );
}

export function usePublishCallFlow() {
  return useCallFlowMutation(
    (id: string) => voiceApi.callFlows.publish(id),
    (id) => id,
  );
}

export function useScheduleCallFlow() {
  return useCallFlowMutation(
    ({ id, publishAt }: { id: string; publishAt: string }) =>
      voiceApi.callFlows.schedule(id, publishAt),
    ({ id }) => id,
  );
}

export function useCancelCallFlowSchedule() {
  return useCallFlowMutation(
    (id: string) => voiceApi.callFlows.cancelSchedule(id),
    (id) => id,
  );
}

export function useUnpublishCallFlow() {
  return useCallFlowMutation(
    (id: string) => voiceApi.callFlows.unpublish(id),
    (id) => id,
  );
}
